// กองบรรณาธิการ — review customer submissions, confirm payment, approve with an issue date,
// and clean up old data so the database stays small.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  STATUS_LABEL, CATEGORIES, apiUrl,
  adminList, adminUpdate, adminDelete, cleanupPreview, cleanupRun, adminPrintLog, adminSetPrinted, adminSetPrintedMany, adminUploadPaymentQr,
  adminNotifySettings, adminSaveNotifySettings, adminNotifyTest, adminConfirmOrder,
} from "../../services/newspaperApi";
import { thaiDate } from "../../components/site/siteText";
import "./AdminEditor.css";
import DateInput from "../../components/site/DateInput";
import { formatDateTime } from "../../utils/dateFormat";
import { PageNav, PageSummary, usePaging } from "../../components/admin/Pager";
import { loadSiteContentFromServer, saveSiteContent } from "../../services/contentService";

const METHOD_LABEL = { template: "กรอกเอง", file: "PDF / รูปภาพ", excel: "Excel" };
const FILTERS = [["", "ทั้งหมด"], ["submitted", STATUS_LABEL.submitted], ["paid", STATUS_LABEL.paid], ["approved", STATUS_LABEL.approved], ["rejected", STATUS_LABEL.rejected]];

const fmtDateTime = (iso) => (iso ? formatDateTime(iso) : "-");
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const tomorrow = () => new Date(Date.now() + 7 * 3600e3 + 86400e3).toISOString().slice(0, 10);

const formFrom = (item) => ({
  title: item.title || "",
  companyName: item.companyName || "",
  category: item.category || CATEGORIES[CATEGORIES.length - 1],
  summary: item.summary || "",
  issueDate: item.issueDate || item.requestedDate || tomorrow(),
  paymentNote: item.paymentNote || "",
  adminNote: item.adminNote || "",
});

// several notices paid together (order "OD…"): the total and one button for the whole order's payment
function OrderBox({ item, onReload }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const confirmAll = async () => {
    if (!window.confirm(`ยืนยันชำระเงินทั้งรายการ ${item.orderCode} (${item.orderCount} เรื่อง ยอด ${Number(item.orderTotal).toLocaleString("th-TH")} บาท)?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      const { changed } = await adminConfirmOrder(item.orderCode);
      setMsg({ ok: true, text: `ยืนยันชำระแล้ว ${changed} เรื่อง · อนุมัติและกำหนดวันลงฉบับทีละเรื่องได้เลย` });
      onReload();
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ed-order">
      <p className="eyebrow">รายการชำระเงิน {item.orderCode}</p>
      <p>
        {item.orderCount} เรื่อง{item.orderCopies ? ` · หนังสือพิมพ์ฉบับพิมพ์ ${item.orderCopies} เล่ม` : ""} · ยอดรวม{" "}
        <strong>{Number(item.orderTotal || 0).toLocaleString("th-TH")} บาท</strong>
        {item.orderAt ? ` · ส่งเมื่อ ${fmtDateTime(item.orderAt)}` : ""}
      </p>
      {item.status === "submitted" && (
        <button type="button" className="button outline" disabled={busy} onClick={confirmAll}>
          ยืนยันชำระเงินทั้งรายการ ({item.orderCount} เรื่อง)
        </button>
      )}
      {msg && <p className={`adm-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</p>}
    </div>
  );
}

function Detail({ item, onSaved, onDeleted, onReload }) {
  const [form, setForm] = useState(() => formFrom(item));
  const [dateTouched, setDateTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // reset only when another submission is opened — a refreshed copy of the same item
  // (after saving or a list reload) must not wipe the confirmation message or typed edits
  useEffect(() => {
    setForm(formFrom(item));
    setDateTouched(false);
    setMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const set = (k) => (e) => {
    if (k === "issueDate") setDateTouched(true);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const run = async (patch, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      // the suggested issue date is only stored when approving or when the admin picked one,
      // so a plain save / reject never pins an unpaid item to a date that will pass
      const { issueDate, ...rest } = form;
      const sendDate = patch.status === "approved" || dateTouched || item.status === "approved";
      const { item: saved } = await adminUpdate(item.id, { ...rest, ...(sendDate ? { issueDate } : {}), ...patch });
      setForm(formFrom(saved));
      setDateTouched(false);
      setMsg({ ok: true, text: okText });
      onSaved(saved);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`ลบรายการ ${item.trackingCode} และไฟล์แนบทั้งหมด? ลบแล้วกู้คืนไม่ได้`)) return;
    setBusy(true);
    try {
      await adminDelete(item.id);
      onDeleted(item.id);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
      setBusy(false);
    }
  };

  return (
    <div className="adm-panel ed-detail site-form">
      <div className="ed-detail-head">
        <div>
          <p className="eyebrow">{item.trackingCode} · {METHOD_LABEL[item.method] || item.method}</p>
          <h3>{item.title || "ประกาศ"}</h3>
        </div>
        <div className="ed-badges">
          <span className={`adm-badge ${item.status}`}>{STATUS_LABEL[item.status]}</span>
          {item.isTest && <span className="adm-badge test">ทดสอบ</span>}
          {item.status === "submitted" && (item.slips || []).length > 0 && <span className="adm-badge slip">แนบสลิปแล้ว</span>}
        </div>
      </div>

      <dl className="ed-facts">
        <div><dt>ผู้ติดต่อ</dt><dd>{item.contactName || "-"}</dd></div>
        <div><dt>เบอร์โทร</dt><dd>{item.phone ? <a href={`tel:${item.phone}`}>{item.phone}</a> : "-"}</dd></div>
        <div><dt>อีเมล</dt><dd>{item.email || "-"}</dd></div>
        <div><dt>ส่งเมื่อ</dt><dd>{fmtDateTime(item.createdAt)}</dd></div>
        <div><dt>วันที่ลูกค้าต้องการ</dt><dd>{item.requestedDate ? thaiDate(item.requestedDate) : "-"}</dd></div>
        <div><dt>วันลงฉบับ</dt><dd>{item.issueDate ? thaiDate(item.issueDate) : "-"}</dd></div>
      </dl>

      {item.orderCode && <OrderBox item={item} onReload={onReload} />}

      <div className="ed-files">
        <p className="eyebrow">ไฟล์แนบ</p>
        {(item.files || []).length === 0 && <p className="adm-note">ไม่มีไฟล์แนบ</p>}
        {(item.files || []).map((f, i) => (
          <a key={i} className="ed-file" href={apiUrl(f.url)} target="_blank" rel="noreferrer">
            <span>{f.name}</span>
            <small>{fmtSize(f.size)} ↗</small>
          </a>
        ))}
        {(item.slips || []).length > 0 && (
          <>
            <p className="eyebrow ed-slips-title">สลิปการโอนเงินที่ลูกค้าแนบ</p>
            {item.slips.map((s, i) => (
              <a key={i} className="ed-file" href={apiUrl(s.url)} target="_blank" rel="noreferrer">
                <span>{s.name}</span>
                <small>{fmtDateTime(s.at)} · {fmtSize(s.size)} ↗</small>
              </a>
            ))}
            {item.status === "submitted" && <p className="adm-note">ตรวจยอดเงินในสลิปแล้วกด &quot;ยืนยันชำระเงินแล้ว&quot; จากนั้นจึงอนุมัติ</p>}
          </>
        )}
        {item.method === "excel" && (
          <p className="adm-note">ไฟล์ Excel ให้นำเข้าที่เมนู &quot;นำเข้าจาก Excel&quot; (สร้างภาพใบประกาศ) แล้วจึงอนุมัติรายการนี้</p>
        )}
      </div>

      <div className="form-row">
        <label>ชื่อประกาศ<input value={form.title || ""} onChange={set("title")} /></label>
        <label>บริษัท / กิจการ<input value={form.companyName || ""} onChange={set("companyName")} /></label>
      </div>
      <div className="form-row">
        <label>
          ประเภทเนื้อหา
          <select value={form.category || ""} onChange={set("category")}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>วันลงฉบับ<DateInput value={form.issueDate || ""} onChange={set("issueDate")} /></label>
      </div>
      <label>สรุปเนื้อหา (แสดงบนหน้าหนังสือพิมพ์)<textarea rows={3} value={form.summary || ""} onChange={set("summary")} /></label>
      <div className="form-row">
        <label>บันทึกการชำระเงิน<input value={form.paymentNote || ""} onChange={set("paymentNote")} placeholder="เช่น โอนวันที่ / ยอด / แจ้งทาง LINE" /></label>
        <label>บันทึกภายใน<input value={form.adminNote || ""} onChange={set("adminNote")} /></label>
      </div>

      {msg && <p className={`adm-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</p>}

      <div className="ed-actions">
        {item.status === "submitted" && (
          <button type="button" className="button dark" disabled={busy} onClick={() => run({ status: "paid" }, "บันทึกว่าชำระเงินแล้ว")}>
            ยืนยันชำระเงินแล้ว <span>✓</span>
          </button>
        )}
        {(item.status === "paid" || item.status === "approved") && (
          <button type="button" className="button dark" disabled={busy} onClick={() => run({ status: "approved" }, "อนุมัติและกำหนดวันลงฉบับแล้ว")}>
            {item.status === "approved" ? "บันทึกการอนุมัติ" : "อนุมัติ + ลงฉบับ"} <span>↗</span>
          </button>
        )}
        <button type="button" className="button outline" disabled={busy} onClick={() => run({}, "บันทึกแล้ว")}>
          บันทึกข้อมูล
        </button>
        {item.status !== "rejected" && (
          <button type="button" className="text-link" disabled={busy} onClick={() => run({ status: "rejected" }, "ตั้งเป็นไม่อนุมัติแล้ว")}>
            ไม่อนุมัติ
          </button>
        )}
        {item.status !== "submitted" && (
          <button type="button" className="text-link" disabled={busy} onClick={() => run({ status: "submitted" }, "ย้อนกลับเป็นรอชำระเงิน")}>
            ย้อนเป็นรอชำระเงิน
          </button>
        )}
        <button type="button" className="text-link ed-danger" disabled={busy} onClick={remove}>
          ลบรายการ
        </button>
      </div>
    </div>
  );
}

const CLEANUP_TYPES = [
  { type: "test", label: "ลบข้อมูลทดสอบทั้งหมด", hint: "รายการที่สร้างจากเครื่องทดสอบ (ป้าย \"ทดสอบ\") พร้อมไฟล์แนบ" },
  { type: "older_than", label: "ลบประกาศตามช่วงวันที่", hint: "ลบทุกสถานะที่ส่งมาในช่วงวันที่เลือก พร้อมไฟล์แนบ", needs: "range" },
  { type: "unpaid_older", label: "ลบประกาศที่ไม่ได้ชำระเงินค้างนาน", hint: "สถานะ \"รอชำระเงิน\" หรือ \"ไม่อนุมัติ\" ที่ค้างเกินจำนวนวันที่กำหนด", needs: "days" },
  { type: "pdf_older", label: "ลบ PDF หนังสือพิมพ์ตามช่วงวันที่", hint: "ไฟล์ PDF ฉบับรายวันในช่วงวันที่เลือก ทั้งที่สร้างจาก \"สร้าง PDF รายวัน\" และฉบับบนเว็บ (ฉบับบนเว็บสร้างใหม่อัตโนมัติเมื่อมีคนดาวน์โหลด)", needs: "range" },
];

function Cleanup({ onDone }) {
  const [type, setType] = useState("test");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [days, setDays] = useState("30");
  const [preview, setPreview] = useState(null);
  // the rows the admin keeps ticked (owner: "ควรฟิลเตอร์ได้ว่าจะลบข้อมูลส่วนไหน") + a search box over the list
  const [picked, setPicked] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const current = CLEANUP_TYPES.find((t) => t.type === type);
  const params = { type, from, to, days: Number(days) };

  useEffect(() => { setPreview(null); setMsg(null); }, [type, from, to, days]);

  const rows = preview?.rows || [];
  const q = search.trim().toLowerCase();
  const shown = q
    ? rows.filter((r) => [r.kind, r.trackingCode, r.title, r.companyName].join(" ").toLowerCase().includes(q))
    : rows;
  const toggle = (key) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const pickShown = (on) => setPicked((s) => {
    const n = new Set(s);
    shown.forEach((r) => (on ? n.add(r.key) : n.delete(r.key)));
    return n;
  });

  const check = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const p = await cleanupPreview(params);
      setPreview(p);
      setPicked(new Set((p.rows || []).map((r) => r.key))); // everything ticked; untick what to keep
      setSearch("");
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!window.confirm(`ลบ ${picked.size} รายการที่เลือก? ลบแล้วกู้คืนไม่ได้`)) return;
    setBusy(true);
    try {
      const { deleted } = await cleanupRun({ ...params, keys: [...picked] });
      setMsg({ ok: true, text: `ลบแล้ว ${deleted} รายการ` });
      setPreview(null);
      onDone();
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="adm-panel ed-cleanup site-form">
      <p className="eyebrow">DATABASE HOUSEKEEPING</p>
      <h3>ลบข้อมูลเก่า</h3>
      <p className="adm-note">ลดขนาดฐานข้อมูลและพื้นที่ไฟล์ ระบบจะแสดงจำนวนที่จะลบก่อนทุกครั้ง และต้องกดยืนยันอีกครั้งจึงจะลบจริง</p>

      <div className="ed-cleanup-types">
        {CLEANUP_TYPES.map((t) => (
          <label key={t.type} className={`ed-radio${type === t.type ? " active" : ""}`}>
            <input type="radio" name="cleanup" checked={type === t.type} onChange={() => setType(t.type)} />
            <span>
              <strong>{t.label}</strong>
              <small>{t.hint}</small>
            </span>
          </label>
        ))}
      </div>

      {current.needs === "range" && (
        <div className="ed-range">
          <label className="ed-param">ตั้งแต่วันที่<DateInput value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="ed-param">ถึงวันที่<DateInput value={to} onChange={(e) => setTo(e.target.value)} /></label>
          <small className="adm-note">รวมวันแรกและวันสุดท้าย · เว้น "ตั้งแต่วันที่" ว่าง = ทุกฉบับจนถึง "ถึงวันที่"</small>
        </div>
      )}
      {current.needs === "days" && (
        <label className="ed-param">ค้างเกิน (วัน)<input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} /></label>
      )}

      {msg && <p className={`adm-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</p>}

      {preview && (
        <div className="ed-preview">
          <p>
            <strong>พบ {rows.length} รายการ</strong>
            {rows.length > 0 ? ` · เลือกจะลบ ${picked.size} รายการ (เอาเครื่องหมายออกจากรายการที่ต้องการเก็บไว้)` : " · ไม่มีอะไรต้องลบ"}
          </p>
          {rows.length > 0 && (
            <>
              <div className="ed-pick-bar">
                <input
                  type="search"
                  placeholder="ค้นหา: รหัส ชื่อเรื่อง บริษัท ประเภท"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button type="button" className="text-link" onClick={() => pickShown(true)}>เลือกที่แสดงทั้งหมด</button>
                <button type="button" className="text-link" onClick={() => pickShown(false)}>ไม่เลือกที่แสดง</button>
              </div>
              <div className="ed-pick-list">
                <table className="adm-table">
                  <thead>
                    <tr><th aria-label="เลือก" /><th>ประเภท</th><th>รหัส / ชื่อ</th><th>สถานะ</th><th>วันที่ลง</th><th>ส่งเมื่อ</th></tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => (
                      <tr key={r.key} className={picked.has(r.key) ? "picked" : ""} onClick={() => toggle(r.key)}>
                        <td>
                          <input type="checkbox" checked={picked.has(r.key)} onChange={() => toggle(r.key)} onClick={(e) => e.stopPropagation()} aria-label={`เลือก ${r.title || r.key}`} />
                        </td>
                        <td>{r.kind}{r.isTest ? <small>ทดสอบ</small> : null}</td>
                        <td>
                          {r.trackingCode && <span className="ed-code">{r.trackingCode} </span>}
                          {r.title || "-"}
                          {r.companyName && <small>{r.companyName}</small>}
                        </td>
                        <td>{r.status ? STATUS_LABEL[r.status] : "-"}</td>
                        <td>{r.issueDate ? thaiDate(r.issueDate) : "-"}</td>
                        <td>{fmtDateTime(r.createdAt)}</td>
                      </tr>
                    ))}
                    {shown.length === 0 && <tr><td colSpan={6}>ไม่พบรายการที่ตรงกับคำค้นหา</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <div className="ed-actions">
        <button type="button" className="button outline" disabled={busy} onClick={check}>ตรวจสอบรายการ</button>
        {preview && picked.size > 0 && (
          <button type="button" className="button dark ed-danger-btn" disabled={busy} onClick={confirmDelete}>
            ลบที่เลือก {picked.size} รายการ <span>×</span>
          </button>
        )}
      </div>
    </div>
  );
}

// Registration no. / ISSN of the paper, typed by the admin (owner 28-10-2569: "เพิ่มช่องทางการกรอกเองได้เพื่อกำหนดค่าเริ่มต้น").
// Saved into site content newspaperPaper (the same fields as "แก้ไขเนื้อหาเว็บ → หัว / ท้ายหนังสือพิมพ์"); the back
// cover shows a line only when it is filled. Every edition — web and PDF — uses the saved values from then on.
const PAPER_FIELDS = [
  ["registrationNo", "เลขทะเบียนหนังสือพิมพ์", "เช่น 1/2569"],
  ["issn", "ISSN", "เช่น 1234-5678"],
];
function PaperIdentity() {
  const [paper, setPaper] = useState(null);
  const [form, setForm] = useState({ registrationNo: "", issn: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    loadSiteContentFromServer()
      .then((c) => {
        const p = c.newspaperPaper || {};
        setPaper(p);
        setForm({ registrationNo: p.registrationNo || "", issn: p.issn || "" });
      })
      .catch((e) => setMsg({ ok: false, text: `โหลดข้อมูลไม่สำเร็จ: ${e.message}` }));
  }, []);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const next = { ...paper, registrationNo: form.registrationNo.trim(), issn: form.issn.trim() };
      const saved = await saveSiteContent({ newspaperPaper: next }); // the server merges per section
      setPaper(saved.newspaperPaper || next);
      setMsg({ ok: true, text: "บันทึกแล้ว · ปกหลังของทุกฉบับใช้ค่านี้ตั้งแต่ตอนนี้" });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const changed = paper && PAPER_FIELDS.some(([k]) => (form[k] || "").trim() !== (paper[k] || ""));
  return (
    <div className="adm-panel ed-paper-id site-form">
      <p className="eyebrow">ข้อมูลหนังสือพิมพ์</p>
      <h3>เลขทะเบียน และ ISSN</h3>
      <p className="adm-note">แสดงที่ปกหลังของหนังสือพิมพ์ทุกฉบับ (บนเว็บและ PDF) · เว้นว่าง = ยังไม่แสดง</p>
      <div className="ed-range">
        {PAPER_FIELDS.map(([key, label, hint]) => (
          <label key={key} className="ed-param">
            {label}
            <input
              type="text"
              maxLength={60}
              placeholder={hint}
              value={form[key]}
              disabled={!paper}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      {msg && <p className={`adm-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</p>}
      <div className="ed-actions">
        <button type="button" className="button dark" disabled={busy || !changed} onClick={save}>บันทึก</button>
      </div>
    </div>
  );
}

// Payment channels shown to customers after they submit (owner 28-10-2569: "เพิ่มช่องทางการกำหนดเป็นข้อมูลอัพเดท
// สำหรับ Admin เรื่อง เลขบัญชี + QR Payment"). Saved into site content paymentInfo; the QR picture is uploaded first
// (public file) and its URL saved with the rest. Empty fields are simply not shown.
const PAY_FIELDS = [
  ["pricePerNotice", "ค่าลงประกาศ (บาท / เรื่อง)", "50"],
  ["pricePerCopy", "หนังสือพิมพ์ฉบับพิมพ์ (บาท / เล่ม)", "150"],
  ["promptPayId", "พร้อมเพย์ (เบอร์โทร / เลขผู้เสียภาษี)", "เช่น 0960670928"],
  ["promptPayName", "ชื่อบัญชีพร้อมเพย์", "เช่น บจก. มณีนพเก้า นิวส์กรุ๊ป"],
  ["bankName", "ธนาคาร", "เช่น ธนาคารกสิกรไทย สาขา…"],
  ["accountNo", "เลขบัญชี", "เช่น 123-4-56789-0"],
  ["accountName", "ชื่อบัญชี", "เช่น บริษัท มณีนพเก้า นิวส์กรุ๊ป จำกัด"],
];
const PAY_KEYS = [...PAY_FIELDS.map(([k]) => k), "qrUrl"];
function PaymentSettings() {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    loadSiteContentFromServer()
      .then((c) => {
        const p = c.paymentInfo || {};
        setSaved(p);
        setForm(Object.fromEntries(PAY_KEYS.map((k) => [k, p[k] || ""])));
      })
      .catch((e) => setMsg({ ok: false, text: `โหลดข้อมูลไม่สำเร็จ: ${e.message}` }));
  }, []);

  const uploadQr = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) { setMsg({ ok: false, text: "รูป QR ต้องเป็นไฟล์ PNG หรือ JPG" }); return; }
    if (file.size > 2 * 1024 * 1024) { setMsg({ ok: false, text: "รูป QR ใหญ่เกิน 2 MB" }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const { url } = await adminUploadPaymentQr(file);
      setForm((f) => ({ ...f, qrUrl: url }));
      setMsg({ ok: true, text: "อัปโหลดรูป QR แล้ว · กด \"บันทึก\" เพื่อใช้รูปนี้" });
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const next = { ...saved, ...Object.fromEntries(PAY_KEYS.map((k) => [k, String(form[k] || "").trim()])) };
      const result = await saveSiteContent({ paymentInfo: next }); // the server merges per section
      setSaved(result.paymentInfo || next);
      setMsg({ ok: true, text: "บันทึกแล้ว · ลูกค้าเห็นช่องทางนี้หลังกดส่งประกาศ และที่หน้าติดตามสถานะ" });
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const changed = saved && form && PAY_KEYS.some((k) => String(form[k] || "").trim() !== (saved[k] || ""));
  return (
    <div className="adm-panel ed-paper-id site-form">
      <p className="eyebrow">PAYMENT</p>
      <h3>ช่องทางชำระเงิน</h3>
      <p className="adm-note">แสดงให้ลูกค้าเห็นหลังกดส่งประกาศ และที่หน้าติดตามสถานะ (ระหว่างรอชำระเงิน) พร้อมช่องแนบสลิป · เว้นว่าง = ไม่แสดงช่องนั้น</p>
      {form && (
        <>
          <div className="ed-pay-qr">
            {form.qrUrl ? <img src={apiUrl(form.qrUrl)} alt="QR พร้อมเพย์" /> : <p className="adm-note">ยังไม่มีรูป QR</p>}
            <div>
              <label className="button outline">
                {form.qrUrl ? "เปลี่ยนรูป QR" : "อัปโหลดรูป QR พร้อมเพย์"}
                <input type="file" accept=".png,.jpg,.jpeg" hidden disabled={busy} onChange={uploadQr} />
              </label>
              {form.qrUrl && (
                <button type="button" className="text-link ed-danger" disabled={busy} onClick={() => setForm((f) => ({ ...f, qrUrl: "" }))}>
                  เอารูป QR ออก
                </button>
              )}
              <small className="adm-note">PNG / JPG ไม่เกิน 2 MB</small>
            </div>
          </div>
          <div className="ed-range">
            {PAY_FIELDS.map(([key, label, hint]) => (
              <label key={key} className="ed-param">
                {label}
                <input type="text" maxLength={120} placeholder={hint} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
              </label>
            ))}
          </div>
        </>
      )}
      {msg && <p className={`adm-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</p>}
      <div className="ed-actions">
        <button type="button" className="button dark" disabled={busy || !changed} onClick={save}>บันทึก</button>
      </div>
    </div>
  );
}

// Who hears about new notices and payment slips (owner 28-10-2569: e-mail + LINE). E-mail addresses are private
// (not in the public site content); LINE is switched on by env vars in Plesk, so this panel only shows its state.
const LINE_KIND = { user: "บัญชีส่วนตัว", group: "กลุ่ม", room: "ห้องแชท" };

// Groups / rooms the LINE bot is in (seen by the webhook) — the admin copies an id into LINE_NOTIFY_TO
function LineGroups({ sources }) {
  const [copied, setCopied] = useState("");
  const copy = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(id);
    } catch (e) {
      window.prompt("คัดลอก ID นี้", id);
    }
  };
  if (!sources.length) {
    return (
      <p className="adm-note">
        ส่งเข้ากลุ่ม LINE: เปิด "อนุญาตให้เข้าร่วมแชทกลุ่ม" ใน LINE Official Account Manager แล้วเชิญบัญชี LINE OA เข้ากลุ่ม
        — Group ID จะขึ้นที่นี่ (และบอทตอบ ID ในกลุ่ม) จากนั้นนำไปใส่ใน LINE_NOTIFY_TO
      </p>
    );
  }
  return (
    <div className="ed-line-groups">
      <p className="adm-note">กลุ่มที่บัญชี LINE OA อยู่ (นำ ID ไปใส่ใน LINE_NOTIFY_TO ถ้าใส่หลายค่าให้คั่นด้วยจุลภาค สูงสุด 5 ค่า)</p>
      <ul>
        {sources.map((g) => (
          <li key={g.id}>
            <span className="ed-line-kind">{LINE_KIND[g.type] || g.type}</span>
            <code>{g.id}</code>
            {g.active ? <span className="adm-badge approved">รับแจ้งเตือนอยู่</span> : null}
            <small>พบล่าสุด {formatDateTime(g.lastSeenAt)}</small>
            <button type="button" className="button outline" onClick={() => copy(g.id)}>
              {copied === g.id ? "คัดลอกแล้ว" : "คัดลอก ID"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const describe = (r) => (!r ? "-" : r.ok ? "ส่งแล้ว ✓" : r.skipped ? `ไม่ได้ส่ง: ${r.skipped}` : `ผิดพลาด: ${r.error}`);
function NotifySettings() {
  const [state, setState] = useState(null);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    adminNotifySettings()
      .then((s) => { setState(s); setEmails((s.emails || []).join(", ")); })
      .catch((e) => setMsg({ ok: false, text: `โหลดข้อมูลไม่สำเร็จ: ${e.message}` }));
  }, []);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const s = await adminSaveNotifySettings(emails);
      setState(s);
      setEmails(s.emails.join(", "));
      setMsg({ ok: true, text: s.emails.length ? `บันทึกแล้ว · แจ้งเตือนไปที่ ${s.emails.length} อีเมล` : "บันทึกแล้ว · ไม่มีอีเมลรับแจ้งเตือน" });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await adminNotifyTest();
      setMsg({ ok: Boolean(r.mail?.ok || r.line?.ok), text: `อีเมล: ${describe(r.mail)} · LINE: ${describe(r.line)}` });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const changed = state && emails.split(/[,;\s]+/).filter(Boolean).join(",").toLowerCase() !== (state.emails || []).join(",");
  return (
    <div className="adm-panel ed-paper-id site-form">
      <p className="eyebrow">NOTIFICATIONS</p>
      <h3>แจ้งเตือนแอดมิน</h3>
      <p className="adm-note">ระบบแจ้งทันทีเมื่อลูกค้าส่งประกาศใหม่ และเมื่อลูกค้าแนบสลิปชำระเงิน (รายการทดสอบจากเครื่อง local จะไม่แจ้ง)</p>
      <label className="ed-param ed-notify-emails">
        อีเมลรับแจ้งเตือน (ใส่ได้สูงสุด 5 อีเมล คั่นด้วยจุลภาค)
        <input type="text" placeholder="เช่น admin@maneenopkaogroup.co.th, owner@gmail.com" value={emails} disabled={!state} onChange={(e) => setEmails(e.target.value)} />
      </label>
      {state && (
        <ul className="ed-notify-state">
          <li>อีเมล: {state.mail ? "เซิร์ฟเวอร์พร้อมส่ง" : "เครื่องนี้ส่งอีเมลไม่ได้ (ส่งได้บนเว็บจริงเท่านั้น)"}</li>
          <li>
            LINE: {state.line
              ? `ตั้งค่าแล้ว · ส่งถึง ${(state.lineTargets || []).map((t) => LINE_KIND[t] || t).join(", ")}`
              : "ยังไม่ได้ตั้งค่า — ใส่ LINE_CHANNEL_ACCESS_TOKEN และ LINE_NOTIFY_TO ในไฟล์ /nodeapp/.env แล้วกด Restart App"}
          </li>
          <li>
            Webhook (หา Group ID): {state.lineWebhook
              ? "พร้อม · Webhook URL = https://maneenopkaogroup.co.th/api/line/webhook"
              : "ยังไม่ได้ใส่ LINE_CHANNEL_SECRET ใน /nodeapp/.env"}
          </li>
        </ul>
      )}
      {state && <LineGroups sources={state.lineSources || []} />}
      {msg && <p className={`adm-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</p>}
      <div className="ed-actions">
        <button type="button" className="button dark" disabled={busy || !changed} onClick={save}>บันทึก</button>
        <button type="button" className="button outline" disabled={busy || !state} onClick={test}>ส่งทดสอบ</button>
      </div>
    </div>
  );
}

// Owner rule (27-10-2569): when an edition closes, print the real copy and keep it for inspection. Lists the closed
// editions (date passed, PDF open) — not printed yet first — with the PDF and a "พิมพ์เก็บแล้ว" mark.
// filter (owner 28-10-2569: "ให้โดมทำการฟิลเตอร์"): status, date range, issue number; mark everything shown at once
const PRINT_TABS = [["pending", "ยังไม่ได้พิมพ์"], ["printed", "พิมพ์แล้ว"], ["all", "ทั้งหมด"]];
function PrintArchive() {
  const [data, setData] = useState({ pending: 0, editions: [] });
  const [tab, setTab] = useState("pending");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [issue, setIssue] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await adminPrintLog());
      setMsg("");
    } catch (e) {
      setMsg(e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mark = async (date, printed) => {
    setBusy(date);
    try {
      await adminSetPrinted(date, printed);
      await load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy("");
    }
  };

  const editions = data.editions;
  const counts = { pending: editions.filter((e) => !e.printedAt).length, printed: editions.filter((e) => e.printedAt).length, all: editions.length };
  const issueNo = issue.replace(/\D/g, "");
  const rows = editions.filter((e) =>
    (tab === "all" || (tab === "printed" ? e.printedAt : !e.printedAt))
    && (!from || e.date >= from)
    && (!to || e.date <= to)
    && (!issueNo || String(e.issueNo) === issueNo));
  const filtered = Boolean(from || to || issueNo);
  const clear = () => { setFrom(""); setTo(""); setIssue(""); };
  const paging = usePaging(rows, "mng_print_page_size", `${tab}|${from}|${to}|${issue}`);
  const shown = paging.shown;
  const unprinted = shown.filter((e) => !e.printedAt);
  const printedShown = shown.filter((e) => e.printedAt);

  // one request for every edition on this page (single PATCHes in parallel would overwrite each other's log)
  const markShown = async (printed) => {
    const dates = (printed ? unprinted : printedShown).map((e) => e.date);
    if (!dates.length) return;
    const question = printed ? `ทำเครื่องหมาย "พิมพ์เก็บแล้ว" ${dates.length} ฉบับในหน้านี้?` : `ยกเลิก "พิมพ์เก็บแล้ว" ${dates.length} ฉบับในหน้านี้?`;
    if (!window.confirm(question)) return;
    setBusy("many");
    try {
      await adminSetPrintedMany(dates, printed);
      await load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="adm-panel ed-print site-form">
      <p className="eyebrow">ปิดเล่ม · พิมพ์ฉบับจริงเก็บไว้อ้างอิง</p>
      <h3>{data.pending > 0 ? `ยังไม่ได้พิมพ์เก็บ ${data.pending} ฉบับ` : "พิมพ์เก็บครบทุกฉบับแล้ว"}</h3>
      <p className="adm-note">ทุกฉบับที่ปิดเล่มแล้ว (เลยวันลงฉบับ) ต้องพิมพ์ฉบับจริงเก็บไว้เพื่อการตรวจสอบ · เปิด PDF แล้วสั่งพิมพ์ จากนั้นกด &quot;พิมพ์เก็บแล้ว&quot;</p>
      {msg && <p className="adm-msg err">{msg}</p>}

      <div className="ed-filters" role="tablist">
        {PRINT_TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={`ed-filter${tab === key ? " active" : ""}`} onClick={() => setTab(key)}>
            {label} <span>{counts[key]}</span>
          </button>
        ))}
      </div>
      <div className="ed-range ed-print-filter">
        <label className="ed-param">ตั้งแต่วันที่<DateInput value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="ed-param">ถึงวันที่<DateInput value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <label className="ed-param">ฉบับที่<input type="text" inputMode="numeric" placeholder="เช่น 25" value={issue} onChange={(e) => setIssue(e.target.value)} /></label>
        {filtered && <button type="button" className="text-link" onClick={clear}>ล้างตัวกรอง</button>}
      </div>
      <PageSummary paging={paging} unit="ฉบับ" note={filtered ? ` (กรองจาก ${counts[tab]})` : ""} />
      {rows.length === 0 && <p className="adm-note">ไม่มีฉบับที่ตรงกับตัวกรอง</p>}
      {rows.length > 0 && (
        <ul className="ed-print-list">
          {shown.map((e) => (
            <li key={e.date}>
              <span>ฉบับที่ {e.issueNo} ปีที่ {e.volume} · {thaiDate(e.date)}</span>
              <a className="text-link" href={apiUrl(e.pdf)} target="_blank" rel="noreferrer">เปิด PDF ↗</a>
              {e.printedAt ? (
                <button type="button" className="text-link" disabled={Boolean(busy)} onClick={() => mark(e.date, false)}>
                  พิมพ์เก็บแล้ว {formatDateTime(e.printedAt)} · ยกเลิก
                </button>
              ) : (
                <button type="button" className="button dark" disabled={Boolean(busy)} onClick={() => mark(e.date, true)}>
                  พิมพ์เก็บแล้ว ✓
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <PageNav paging={paging} />
      {unprinted.length > 0 && (
        <div className="ed-actions">
          <button type="button" className="button outline" disabled={Boolean(busy)} onClick={() => markShown(true)}>
            พิมพ์เก็บแล้วทั้งหมดในหน้านี้ ({unprinted.length} ฉบับ)
          </button>
        </div>
      )}
      {tab === "printed" && printedShown.length > 0 && (
        <div className="ed-actions">
          <button type="button" className="text-link ed-danger" disabled={Boolean(busy)} onClick={() => markShown(false)}>
            ยกเลิก &quot;พิมพ์เก็บแล้ว&quot; ทั้งหมดในหน้านี้ ({printedShown.length} ฉบับ)
          </button>
        </div>
      )}
    </div>
  );
}

function AdminEditor() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");             // what is typed
  const [appliedQ, setAppliedQ] = useState(""); // what was searched (on submit only)
  const [data, setData] = useState({ items: [], counts: {}, total: 0, testCount: 0 });
  const paging = usePaging(data.items, "mng_submissions_page_size", `${status}|${appliedQ}`);
  const [selectedId, setSelectedId] = useState(null);
  // last known copy of the open item: after a status change it may drop out of the filtered list,
  // and the detail panel (with its "saved" message) must stay open
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current; // ignore answers that arrive after a newer request
    setLoading(true);
    setError("");
    try {
      const result = await adminList(status, appliedQ);
      if (seq === requestSeq.current) setData(result);
    } catch (e) {
      if (seq === requestSeq.current) setError(e.message);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [status, appliedQ]);

  useEffect(() => { load(); }, [load]);

  const selected = data.items.find((it) => it.id === selectedId) || (selectedItem?.id === selectedId ? selectedItem : null);

  const onSaved = (saved) => {
    setSelectedItem(saved);
    setData((d) => ({ ...d, items: d.items.map((it) => (it.id === saved.id ? saved : it)) }));
    load();
  };
  const onDeleted = (id) => {
    setSelectedId(null);
    setData((d) => ({ ...d, items: d.items.filter((it) => it.id !== id) }));
    load();
  };

  return (
    <div className="ed-root">
      <div className="ed-filters">
        {FILTERS.map(([value, label]) => (
          <button
            key={value || "all"}
            type="button"
            className={`ed-filter${status === value ? " active" : ""}`}
            onClick={() => { setStatus(value); setSelectedId(null); }}
          >
            {label}
            <span>{value ? data.counts[value] || 0 : data.total}</span>
          </button>
        ))}
      </div>

      <form className="ed-search site-form" onSubmit={(e) => { e.preventDefault(); if (q.trim() === appliedQ) load(); else setAppliedQ(q.trim()); }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ค้นหาเลขติดตาม ชื่อประกาศ บริษัท ชื่อ หรือเบอร์โทร" />
        <button type="submit" className="button dark">ค้นหา</button>
      </form>

      {error && <p className="adm-msg err">{error}</p>}

      <div className="adm-panel ed-list">
        {loading && <p className="adm-note">กำลังโหลด...</p>}
        {!loading && data.items.length === 0 && <p className="adm-note">ยังไม่มีรายการในสถานะนี้</p>}
        {!loading && data.items.length > 0 && <PageSummary paging={paging} />}
        {!loading && data.items.length > 0 && (
          <table className="adm-table">
            <thead>
              <tr><th>เลขติดตาม</th><th>ประกาศ</th><th>ผู้ติดต่อ</th><th>ส่งเมื่อ</th><th>สถานะ</th></tr>
            </thead>
            <tbody>
              {paging.shown.map((it) => (
                <tr key={it.id} className={it.id === selectedId ? "selected" : ""} onClick={() => { setSelectedId(it.id); setSelectedItem(it); }}>
                  <td className="ed-code">{it.trackingCode}{it.orderCode && <small>{it.orderCode}</small>}</td>
                  <td>
                    <strong>{it.title || "-"}</strong>
                    <small>{[it.companyName, METHOD_LABEL[it.method]].filter(Boolean).join(" · ")}</small>
                  </td>
                  <td>{it.contactName}<small>{it.phone}</small></td>
                  <td>{fmtDateTime(it.createdAt)}</td>
                  <td>
                    <span className={`adm-badge ${it.status}`}>{STATUS_LABEL[it.status]}</span>
                    {it.isTest && <span className="adm-badge test">ทดสอบ</span>}
                    {it.status === "submitted" && (it.slips || []).length > 0 && <span className="adm-badge slip">แนบสลิปแล้ว</span>}
                    {it.status === "approved" && it.issueDate && <small>ลงฉบับ {thaiDate(it.issueDate)}</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && <PageNav paging={paging} />}
      </div>

      {selected && <Detail item={selected} onSaved={onSaved} onDeleted={onDeleted} onReload={load} />}

      <PrintArchive />

      <NotifySettings />

      <PaymentSettings />

      <PaperIdentity />

      <Cleanup onDone={load} />
    </div>
  );
}

export default AdminEditor;
